<template>
  <b-container class="form-action-plan" fluid>
    <header v-if="!plan.image" class="step row pt-3 pl-2">
      <h3>{{ labels.heading }}</h3>
    </header>
    <div class="step row p-0" v-else>
      <div
        class="col col-md-12 image-plan"
        :style="{ 'background-image': `url('${plan.image}')` }"
      ></div>
    </div>

    <!-- STEP - Title Description -->
    <b-row class="step">
      <b-col class="col-md-9">
        <custom-input
          ref="planTitle"
          v-model="plan.title"
          name="planTitle"
          :state="validate.title"
          :placeholder="labels.addTitle"
          :refProp="'planTitle'"
        />

        <custom-textarea
          v-model="plan.description"
          :placeholder="labels.addDescription"
        />
      </b-col>
    </b-row>
    <!-- STEP - Title Description -->

    <!-- STEP - Group Option -->
    <form-group
      @setGroupId="setGroupId"
      :groupId="plan.groupId"
      :groupList="groupList"
      :invalidField="
        validate.group ===
          false /** don't use !validate.group because initial value is undefined*/
      "
      :isEdit="isEdit"
    />
    <!-- END - STEP Group Option -->

    <!-- STEP - Metric Option List -->
    <form-metrics
      :isEdit="isEdit"
      :plan="plan"
      :metrics="metrics"
      @setSelectedMetric="setSelectedMetric"
      :loading="loadingMetrics"
      :invalidFields="
        validate.metric ===
          false /** don't use !validate.group because initial value is undefined*/
      "
    />
    <!-- END - STEP Metric Option List -->

    <!--STEP Tasks -->
    <b-row
      class="step tasks"
      :class="{
        'disabled-row': !isMetricSelected,
        'invalid-fields':
          validate.tasks ===
          false /** don't use !validate.group because initial value is undefined*/,
      }"
    >
      <header class="col col-md-9 col-12 d-flex align-items-center">
        <div class="number">3</div>
        <span class="text-primary medium">{{ labels.tasks }}</span>
      </header>
      <div
        id="tasksCollapse"
        v-if="isMetricSelected"
        class="tasks-wrap col col-12"
      >
        <div class="wrap-tasks">
          <TaskList :tasks="plan.tasks" :focusOnTask="focusOnTask" />
        </div>
      </div>
    </b-row>
    <!-- END - STEP Tasks -->

    <!-- STEP Goal -->
    <form-goal
      :metric="plan.metric"
      :goal="plan.goal"
      @change="setGoal"
      :disabledInput="isEdit"
      :hideGoal="planGoalDisabled"
      :class="{ 'disabled-row': planGoalDisabled }"
    />
    <!-- END - Step goal -->

    <!--STEP Expires At -->
    <b-row
      class="step row wrap-expires-at flex flex-wrap"
      :class="{ 'disabled-row': expiresAtDisabled }"
    >
      <header class="col col-md-6 flex">
        <div class="number">5</div>
        <span class="text-primary medium flex-grow">{{
          labels.expiresAt
        }}</span>
      </header>
      <b-col class="col-12 col-md-3 text-center mt-3">
        <custom-input
          ref="dateExpiresAt"
          refProp="dateExpiresAt"
          name="date"
          :state="validate.dateState"
          :hasMask="true"
          :hasLabel="false"
          :disabled="isEdit"
          :class="{ 'd-none': expiresAtDisabled }"
          v-model="expiresAt"
          placeholder="00/00/0000"
        />
      </b-col>
    </b-row>
    <!-- END - STEP  Expires At -->

    <!-- FOOTER  Submit -->
    <footer class="row">
      <b-col class="d-flex align-items-center">
        <span class="text-danger">
          {{ formError }}
        </span>
      </b-col>
      <b-col class="col-md-6 col-12 text-right p-0">
        <b-button class="btn" variant="outline-link" @click="$emit('close')">
          {{ labels.cancel }}
        </b-button>
        <b-button
          class="btn pl-5 pr-5"
          variant="tertiary"
          @click="saveActionPlan"
        >
          <i v-if="loadingSave" class="mdi mdi-loading mdi-spin pr-2"></i>
          {{ labels.ok }}
        </b-button>
      </b-col>
    </footer>
    <!-- END - FOOTER  Submit -->
  </b-container>
</template>

<script>
import CustomInput from '@/components/Shared/Form/CustomInput.vue'
import CustomTextarea from '@/components/Shared/Form/CustomTextarea.vue'
import TaskList from '@/components/ActionPlan/TaskList.vue'
import FormMetrics from '@/components/ActionPlan/FormMetrics.vue'
import FormGroup from '@/components/ActionPlan/FormGroup.vue'
import FormGoal from '@/components/ActionPlan/FormGoal.vue'
import { mask } from 'vue-the-mask'
import { HIGH } from '@/helpers/messages-priority'
import debounce from '@/helpers/debounce'
import { mapGetters } from 'vuex'
import { getReportsByGroup } from '@/service/reports'

const props = {
  dataPlan: {
    type: Object,
    required: false,
  },
}

const emptyPlan = () => ({
  _id: '',
  image: '',
  title: '',
  description: '',
  metric: {},
  expiresAt: '',
  templateId: undefined,
  goal: undefined,
  tasks: [],
})

const copy = obj => JSON.parse(JSON.stringify(obj))

export default {
  props,
  directives: { mask },
  components: {
    CustomInput,
    CustomTextarea,
    TaskList,
    FormMetrics,
    FormGroup,
    FormGoal,
  },

  data() {
    return {
      formError: undefined,
      loadingSave: false,
      focusOnTask: undefined,
      expiresAt: undefined,
      plan: emptyPlan(),
      metrics: [],
      validate: {
        title: undefined,
        dateState: undefined,
        group: undefined,
        metric: undefined,
      },
      loadingMetrics: false,
    }
  },

  watch: {
    'plan.title'() {
      this.validate.title = undefined
      this.formError = this.validateLabel(this.labels.validate.title)
    },
    'plan.expiresAt'(value) {
      this.updateExpiresAt(value)
    },
    'plan.groupId'(value) {
      this.validate.group = undefined
      this.loadMetrics(value)
    },
    'plan.metric'() {
      this.validate.metric = undefined
    },
    'plan.tasks': {
      handler() {
        this.validate.tasks = undefined
      },
      deep: true,
    },
    expiresAt() {
      this.validate.dateState = undefined
    },
    storePlan(value) {
      this.setPlan(copy(value))
    },
  },
  created() {
    this.loadPlan()

    if (this.dataPlan && this.dataPlan.metric)
      this.setSelectedMetric(this.dataPlan.metric)
    if (this.dataPlan && this.dataPlan.groupId)
      this.setGroupId(this.dataPlan.groupId)

    this.loadGroups()
    this.loadMetrics(this.plan.groupId)
  },

  beforeDestroy() {
    this.setPlan(emptyPlan())
  },

  computed: {
    ...mapGetters({
      storePlan: 'actionPlan/actionPlan',
    }),
    isEdit() {
      return !!this.plan._id
    },
    labels() {
      const form = this.$store.getters.resources.component.actionPlan.form
      const labels = { ...form, ...(this.isEdit ? form.edit : form.new) }

      return {
        ...labels,
      }
    },
    isValidDate() {
      if (!this.expiresAt) return false
      const date = this.$moment(
        this.expiresAt
          .split('/')
          .reverse()
          .join('-')
      )
      return date > this.$moment(new Date())
    },
    isMetricSelected() {
      return !!this.plan.metric._id
    },
    expiresAtDisabled() {
      return !this.isMetricSelected
    },
    planGoalDisabled() {
      return !this.isMetricSelected
    },
    groupList() {
      return this.$store.getters['user/groupList'].filter(g => !g.inactive)
    },
  },

  methods: {
    loadPlan() {
      const plan = copy(this.storePlan)
      plan.groupId = plan.groupId || this.$route.params.groupId
      this.setPlan(plan)
    },
    async loadMetrics(groupId) {
      this.loadingMetrics = true
      const { metrics } = groupId
        ? await getReportsByGroup(groupId)
        : { metrics: [] }
      this.metrics = metrics

      if (this.plan.metric && this.plan.metric._id) {
        const metric = this.metrics
          .filter(({ id }) => this.plan.metric._id === id)
          .reduce((acc, cur) => {
            return { ...acc, score: cur.score }
          }, this.plan.metric)

        this.setSelectedMetric(metric)
      }

      this.loadingMetrics = false
    },
    setPlan(plan) {
      this.plan = plan
    },
    setSelectedMetric(metric) {
      this.plan.metric = metric
    },
    saveActionPlan() {
      if (!this.isValidForm()) return false
      this.loadingSave = true
      const plan = {
        ...this.plan,
        expiresAt: this.$moment(
          this.expiresAt
            .split('/')
            .reverse()
            .join('-')
        ),
      }

      const storeAction = this.isEdit
        ? 'actionPlan/updateActionPlan'
        : 'actionPlan/saveActionPlan'

      this.$store
        .dispatch(storeAction, plan)
        .then(this.onSaveSuccess)
        .catch(this.onSaveError)
        .finally(() => {
          this.loadingSave = false
        })
    },
    onSaveSuccess() {
      this.$root.$emit('app:message:show', {
        priority: HIGH,
        text: this.labels.onSaveSuccess,
        status: 'success',
        countDown: 5,
      })
      this.$router.push({ name: 'action.plan' })
      this.$emit('close')
    },
    onSaveError(e) {
      this.$root.$emit('app:message:show', {
        priority: HIGH,
        text: e.response.data.message,
        status: 'danger',
      })
      // eslint-disable-next-line no-console
      console.error('ERROR:', e.response.data.message)
    },
    isValidForm() {
      let error = ''
      const { title, metric, tasks, groupId, goal } = this.plan

      const validTasks = tasks
        .map(({ text }, i) => {
          return (text || '').trim() ? i : undefined
        })
        .filter(i => !isNaN(i))

      switch (true) {
        case !(title || '').trim():
          error = this.labels.validate.title
          this.validate.title = false
          /* istanbul ignore next */
          this.$refs.planTitle.$refs.planTitle.focus()
          break

        case !groupId:
          error = this.labels.validate.group
          this.validate.group = false
          break

        case !metric._id:
          error = this.labels.validate.metric
          this.validate.metric = false
          // this.metricToggle = true
          break

        case !validTasks.length:
          error = this.labels.validate.tasks
          this.focusOnTask = undefined
          debounce(100, () => {
            this.validate.tasks = false
            this.focusOnTask = 0
          })()
          break

        case parseFloat(goal) < parseFloat(metric.score) && !this.isEdit:
          error = this.labels.validate.goal
          break

        case !this.isValidDate && !this.isEdit:
          error = this.labels.validate.date
          this.validate.dateState = false
          /* istanbul ignore next */
          this.$refs.dateExpiresAt.$refs.dateExpiresAt.focus()
      }
      this.applyFormError(error)
      return error === ''
    },
    applyFormError(error) {
      this.formError = undefined

      debounce(100, () => {
        this.formError = error
      })()
    },
    validateLabel(value) {
      return value === this.formError ? undefined : this.formError
    },
    updateExpiresAt(value) {
      if (!value) return false
      this.expiresAt = this.$moment(value).format('DD/MM/YYYY')
      this.validate.dateState = this.isValidDate

      if (this.isValidDate) {
        this.formError = this.validateLabel(this.labels.validate.date)
      }
    },
    loadGroups() {
      !this.groupList.length && this.$store.dispatch('user/loadGroupList')
    },
    setGroupId(id) {
      this.plan.groupId = id
    },
    setGoal(goal) {
      this.plan.goal = goal
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';
@import '~bootstrap/scss/_functions';
@import '~bootstrap/scss/_variables';
@import '~bootstrap/scss/mixins/breakpoints';
@import '~@/styles/custom/mixins';

/deep/ .dropdown-toggle {
  border: 1px solid map-get($theme-colors, 'primary') !important;

  .vs__actions {
    display: none;
  }

  .vs__selected-options {
    padding-left: 15px;
  }
}

.form-action-plan {
  @include media-breakpoint-down(sm) {
    margin-bottom: 3rem;
  }

  header {
    width: initial;
  }

  .image-plan {
    background: map-get($theme-colors, 'gray08');
    min-height: 350px;
    width: 100%;
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;

    @include media-breakpoint-down(sm) {
      min-height: 150px;
    }
  }

  .step {
    align-items: center;
    border-bottom: 1px solid map-get($theme-colors, 'gray01');
    justify-content: center;
    padding: 1rem;

    /deep/ header,
    header {
      padding: 0px;
    }

    .row {
      display: flex;
      justify-content: center;
      padding: 1rem 0;
    }

    /deep/ .number {
      align-items: center;
      background: map-get($theme-colors, 'quinary');
      border-radius: 40px;
      color: white;
      display: inline-flex;
      flex-shrink: 0;
      font-size: 22px;
      justify-content: center;
      height: 45px;
      margin: 0 10px 0 0;
      width: 45px;

      @include media-breakpoint-down(sm) {
        margin-right: 20px;
        min-width: 45px;
      }
    }

    .wrap-tasks {
      margin: 15px 0;
      @include media-breakpoint-down(sm) {
        width: 100%;
      }

      /deep/ .task-list {
        margin: 0 auto;
      }
    }

    .d-grid {
      display: grid;

      .text-wrong {
        color: map-get($theme-colors, 'red');
      }
    }

    .flex,
    /deep/ .flex {
      align-items: center;
      display: flex;
      justify-content: flex-start;

      &.flex-wrap {
        flex-wrap: wrap;
      }

      .flex-grow {
        flex-grow: 1;
      }
    }
    .expires-at {
      border: 1px solid map-get($theme-colors, 'gray01');
      border-radius: 48px;
      height: 48px;
      max-width: 200px;
      text-align: center;
      width: 100%;
    }
  }

  footer {
    margin: 1rem 0;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
  }
}

.disabled-row,
/deep/ .disabled-row {
  header .text-primary {
    color: map-get($theme-colors, 'gray01') !important;
  }

  header .number {
    color: map-get($theme-colors, 'quinary') !important;
    border: 1px dashed map-get($theme-colors, 'quinary') !important;
    background-color: map-get($theme-colors, 'white') !important;
  }
}

.step.tasks.invalid-fields /deep/ .input-group:first-of-type input {
  @include setInvalidInputStyle;
}
</style>
